import { Set } from 'immutable';
import { MULTIPLE } from '../constants/fields';

// The asterisk character is used when bulk-editing items to indicate that
// a field has multiple values among the items that are being edited
export const isAsterisk = (value) => value === '*';

export const multiplePlaceholder = (
  value,
  defaultValue = undefined,
  truncate = false,
) => (isAsterisk(value) ? (truncate ? 'Mult.' : MULTIPLE) : defaultValue);

const mergeArrays = (arrays) => {
  // Confirm that each sub-array has the same number of items
  if (arrays.some((array) => array.length !== arrays[0].length)) return '*';
  // Get the set of ids for items in each sub-array
  const arrayIdsSets = arrays.map((a) => new Set(a.map(({ id }) => id)));
  // Check if the sets of ids are equivalent across all sub-arrays
  const setsEquivalent = arrayIdsSets.every((idsSet) =>
    idsSet.every((id) => arrayIdsSets[0].has(id)),
  );
  // If the sets are equivalent, return the first one
  return setsEquivalent ? arrays[0] : '*';
};

export const mergeObjects = (objects, keys = null) => {
  return (
    keys ||
    // If no keys are provided, create a set from all the keys of all the objects
    new Set(objects.filter((obj) => obj).flatMap((obj) => Object.keys(obj)))
  ).reduce((mergedObject, key) => {
    const keyValues = objects.map((obj) => obj?.[key]);
    // If there is a nested array, merge the items
    if (keyValues[0] != null && Array.isArray(keyValues[0])) {
      return { ...mergedObject, [key]: mergeArrays(keyValues) };
    }
    // If there is a nested object, merge the keys and values of the nested objects
    if (keyValues[0] != null && typeof keyValues[0] === 'object') {
      return { ...mergedObject, [key]: mergeObjects(keyValues) };
    }
    // If all values are equal, set that value on the merged object
    if (keyValues.every((v) => v === keyValues[0])) {
      return { ...mergedObject, [key]: keyValues[0] };
    }
    // Set '*' as the field value on the merged object to indicate it has multiple values
    return { ...mergedObject, [key]: '*' };
  }, {});
};

export const applyBulkEditsToObject = (values, object, keys = null) => {
  if (!values) return object;
  return (keys || Object.keys(values)).reduce((editedObj, key) => {
    const value = values[key];
    // If there is a nested array, overwrite the value
    if (value != null && Array.isArray(value)) {
      return { ...editedObj, [key]: value };
    }
    // If there is a nested object, apply edits to the keys and values of the nested objects
    if (value != null && typeof value === 'object') {
      return {
        ...editedObj,
        [key]: applyBulkEditsToObject(value, editedObj[key]),
      };
    }
    // The value has been edited if it is not an asterisk and differs from the value in the object
    return !isAsterisk(value) && value !== editedObj[key]
      ? { ...editedObj, [key]: value }
      : editedObj;
  }, object);
};
